import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Cpu, Database, Cloud, Shield, Zap } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { createArrayUpdateHelper } from '../utils';

export const TechnologyRequirements: React.FC<SalesQualificationStepProps> = ({
  data,
  onUpdate,
}) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Technology & Integration Requirements
        </h2>
        <p className="text-gray-600">
          Understanding your technical environment helps us design the optimal enterprise
          architecture
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Current Tech Stack */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Cpu className="w-5 h-5 text-blue-600" />
              Current Technology Stack
            </CardTitle>
            <CardDescription>What systems and platforms do you currently use?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Property Management Software (Buildium, AppFolio)',
                'Community Management Platform (Condo Control, CINC)',
                'Financial Management System (QuickBooks, Yardi)',
                'Communication Platform (Mailchimp, Constant Contact)',
                'Document Management (Google Drive, SharePoint)',
                'Website/Portal (HOA/Condo specific platforms)',
                'Security Systems (Access control, cameras)',
                'Maintenance Management (Corrigo, ServiceChannel)',
                'Banking Integration (Online banking platforms)',
                'Custom Developed Solutions',
                'Legacy/Outdated Systems',
                'Minimal Technology Infrastructure',
              ].map((tech) => (
                <div key={tech} className="flex items-start space-x-2">
                  <Checkbox
                    id={`current-tech-${tech}`}
                    checked={data.currentTechStack?.includes(tech) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('currentTechStack', tech, !!checked)
                    }
                  />
                  <Label htmlFor={`current-tech-${tech}`} className="text-sm leading-relaxed">
                    {tech}
                  </Label>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <Label htmlFor="currentTechStackDetails">
                Describe your current tech stack in detail
              </Label>
              <Textarea
                id="currentTechStackDetails"
                value={data.currentTechStackDetails || ''}
                onChange={(e) => updateFormData('currentTechStackDetails', e.target.value)}
                placeholder="e.g., Using Buildium for property management, QuickBooks for accounting, Mailchimp for newsletters, but systems don't integrate..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Integration Needs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="w-5 h-5 text-green-600" />
              Integration Requirements
            </CardTitle>
            <CardDescription>
              What systems need to connect with your enterprise platform?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Financial/Banking Systems Integration',
                'Property Management Software APIs',
                'Communication Platform Sync',
                'Document Management System',
                'Security and Access Control Systems',
                'Maintenance and Work Order Systems',
                'Website and Portal Integration',
                'Mobile App Development',
                'Third-party Vendor Platforms',
                'Government/Municipal Systems',
                'Insurance and Legal Systems',
                'Custom API Development Required',
              ].map((integration) => (
                <div key={integration} className="flex items-start space-x-2">
                  <Checkbox
                    id={`integration-${integration}`}
                    checked={data.integrationNeeds?.includes(integration) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('integrationNeeds', integration, !!checked)
                    }
                  />
                  <Label htmlFor={`integration-${integration}`} className="text-sm leading-relaxed">
                    {integration}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Technical Complexity & Customization */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Customization & Complexity
            </CardTitle>
            <CardDescription>
              How much customization and technical complexity do you need?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Customization Level Required</Label>
              <RadioGroup
                value={data.customizationLevel || ''}
                onValueChange={(value) => updateFormData('customizationLevel', value)}
                className="mt-2"
              >
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="minimal" id="minimal-custom" />
                    <Label htmlFor="minimal-custom" className="text-sm">
                      Minimal - Standard features with basic branding
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="moderate" id="moderate-custom" />
                    <Label htmlFor="moderate-custom" className="text-sm">
                      Moderate - Custom workflows and specialized features
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="extensive" id="extensive-custom" />
                    <Label htmlFor="extensive-custom" className="text-sm">
                      Extensive - Highly customized solution with unique requirements
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="enterprise" id="enterprise-custom" />
                    <Label htmlFor="enterprise-custom" className="text-sm">
                      Enterprise - Fully custom platform with white-label capabilities
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-sm font-medium">Technical Complexity Assessment</Label>
              <RadioGroup
                value={data.technicalComplexity || ''}
                onValueChange={(value) => updateFormData('technicalComplexity', value)}
                className="mt-2"
              >
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="simple" id="simple-tech" />
                    <Label htmlFor="simple-tech" className="text-sm">
                      Simple - Standard business processes and workflows
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="moderate" id="moderate-tech" />
                    <Label htmlFor="moderate-tech" className="text-sm">
                      Moderate - Some complex workflows and data integration
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="complex" id="complex-tech" />
                    <Label htmlFor="complex-tech" className="text-sm">
                      Complex - Multi-entity management with advanced features
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="enterprise" id="enterprise-tech" />
                    <Label htmlFor="enterprise-tech" className="text-sm">
                      Enterprise - Multi-tenant architecture with complex integrations
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Security & Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              Security & Compliance
            </CardTitle>
            <CardDescription>
              Enterprise security and regulatory compliance requirements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'SOC 2 Type II Compliance',
                'GDPR/CCPA Data Privacy Compliance',
                'Multi-factor Authentication (MFA)',
                'Single Sign-On (SSO) Integration',
                'Role-based Access Control (RBAC)',
                'Data Encryption (at rest and in transit)',
                'Regular Security Audits and Penetration Testing',
                'Secure API Gateway and Rate Limiting',
                'Data Backup and Disaster Recovery',
                'Regulatory Reporting and Audit Trails',
                'Custom Security Policies and Controls',
                'On-premise or Private Cloud Deployment',
              ].map((requirement) => (
                <div key={requirement} className="flex items-start space-x-2">
                  <Checkbox
                    id={`security-${requirement}`}
                    checked={data.securityRequirements?.includes(requirement) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('securityRequirements', requirement, !!checked)
                    }
                  />
                  <Label htmlFor={`security-${requirement}`} className="text-sm leading-relaxed">
                    {requirement}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Infrastructure Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Cloud className="w-5 h-5 text-sky-600" />
            Infrastructure & Deployment Preferences
          </CardTitle>
          <CardDescription>
            Where and how would you prefer your enterprise solution to be hosted?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Preferred Deployment Model</Label>
            <RadioGroup
              value={data.scalabilityNeeds || ''}
              onValueChange={(value) => updateFormData('scalabilityNeeds', value)}
              className="mt-2"
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cloud" id="cloud-deploy" />
                  <Label htmlFor="cloud-deploy" className="text-sm">
                    Public Cloud (AWS, Azure, GCP) - Fully managed
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="private_cloud" id="private-cloud" />
                  <Label htmlFor="private-cloud" className="text-sm">
                    Private Cloud - Dedicated infrastructure
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hybrid" id="hybrid-deploy" />
                  <Label htmlFor="hybrid-deploy" className="text-sm">
                    Hybrid - Mix of cloud and on-premise
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="on_premise" id="on-premise" />
                  <Label htmlFor="on-premise" className="text-sm">
                    On-Premise - Full control and data sovereignty
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label className="text-sm font-medium">Scalability Requirements</Label>
            <div className="grid md:grid-cols-2 gap-3 mt-2">
              {[
                'Auto-scaling based on usage',
                'Load balancing and high availability',
                'Multi-region deployment capability',
                'Database scaling and replication',
                'CDN and global content delivery',
                'Microservices architecture',
                'Container orchestration (Kubernetes)',
                'Serverless computing integration',
              ].map((requirement) => (
                <div key={requirement} className="flex items-center space-x-2">
                  <Checkbox
                    id={`scalability-${requirement}`}
                    checked={data.complianceRequirements?.includes(requirement) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('complianceRequirements', requirement, !!checked)
                    }
                  />
                  <Label htmlFor={`scalability-${requirement}`} className="text-sm">
                    {requirement}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline and Implementation */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Implementation Timeline</CardTitle>
            <CardDescription>
              When do you need this enterprise solution operational?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={data.timelineUrgency || ''}
              onValueChange={(value) => updateFormData('timelineUrgency', value)}
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="immediate" id="immediate" />
                  <Label htmlFor="immediate" className="text-sm">
                    Immediate - Need to start within 30 days
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="quarter" id="quarter" />
                  <Label htmlFor="quarter" className="text-sm">
                    This Quarter - Within 90 days
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="half_year" id="half-year" />
                  <Label htmlFor="half-year" className="text-sm">
                    Next 6 Months - Strategic implementation
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="annual" id="annual" />
                  <Label htmlFor="annual" className="text-sm">
                    Next Year - Part of annual planning
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="flexible" id="flexible" />
                  <Label htmlFor="flexible" className="text-sm">
                    Flexible - Right solution more important than timeline
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Custom Development Needs</CardTitle>
            <CardDescription>What custom features or integrations do you require?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Textarea
                value={data.customDevelopmentNeeds || ''}
                onChange={(e) => updateFormData('customDevelopmentNeeds', e.target.value)}
                placeholder="e.g., Custom reporting dashboard, specialized workflow automation, unique business rule integration, third-party API connections..."
                rows={4}
              />

              <div>
                <Label className="text-sm font-medium">Integration Complexity Level</Label>
                <RadioGroup
                  value={data.integrationComplexity || ''}
                  onValueChange={(value) => updateFormData('integrationComplexity', value)}
                  className="mt-2"
                >
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="basic" id="basic-integration" />
                      <Label htmlFor="basic-integration" className="text-sm">
                        Basic - Standard API connections
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="moderate" id="moderate-integration" />
                      <Label htmlFor="moderate-integration" className="text-sm">
                        Moderate - Custom middleware required
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="complex" id="complex-integration" />
                      <Label htmlFor="complex-integration" className="text-sm">
                        Complex - Multiple legacy system connections
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="enterprise" id="enterprise-integration" />
                      <Label htmlFor="enterprise-integration" className="text-sm">
                        Enterprise - Full ecosystem integration
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Help Text */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
        <p className="text-sm text-cyan-700">
          <strong>Technology assessment helps us design your Enterprise architecture:</strong>
        </p>
        <ul className="text-sm text-cyan-700 mt-2 space-y-1">
          <li>• Plan integration strategy with existing systems for seamless transition</li>
          <li>• Design scalable architecture that grows with your community</li>
          <li>• Implement security controls that meet enterprise compliance requirements</li>
          <li>• Create custom development roadmap for specialized features</li>
          <li>• Establish deployment strategy aligned with your IT preferences</li>
        </ul>
      </div>
    </div>
  );
};
